// src/components/Dashboard/PlayerDashboard/WeekGlanceWidget.jsx
// Compact calendar showing events across the next 15 days

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarWeek, FaClock, FaMapMarkerAlt } from 'react-icons/fa';
import styles from './WeekGlanceWidget.module.css';

const DAYS_TO_SHOW = 15;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WeekGlanceWidget = ({ events, programs, selectedProgramId, refreshTrigger }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);

  // Get next 15 days with event counts
  const upcomingDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return Array.from({ length: DAYS_TO_SHOW }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + index);
      date.setHours(0, 0, 0, 0);
      
      const dateStr = date.toDateString();
      const dayOfWeek = date.getDay();
      
      const dayEvents = (events || []).filter(e => {
        const eventDate = new Date(e.dateTime?.start);
        return eventDate.toDateString() === dateStr;
      });
      
      const isToday = index === 0;
      const isSelected = selectedDate && dateStr === selectedDate.toDateString();
      
      return { 
        dayName: DAY_NAMES[dayOfWeek],
        date, 
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        isToday, 
        isSelected,
        events: dayEvents,
        eventCount: dayEvents.length,
        hasEvent: dayEvents.length > 0,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6
      };
    });
  }, [events, selectedDate]);

  // Get events for selected day
  const selectedDayEvents = useMemo(() => {
    if (!selectedDate) return [];
    const selectedDateStr = selectedDate.toDateString();
    return (events || [])
      .filter(e => new Date(e.dateTime?.start).toDateString() === selectedDateStr)
      .sort((a, b) => new Date(a.dateTime?.start) - new Date(b.dateTime?.start));
  }, [events, selectedDate]);

  const handleDayClick = (date, hasEvent) => {
    if (selectedDate && date.toDateString() === selectedDate.toDateString()) {
      setSelectedDate(null); // Toggle off
    } else {
      setSelectedDate(new Date(date.getTime()));
    }
  };

  const handleEventClick = (event) => {
    navigate(`/program/${event.programId}/events?eventId=${event._id}`);
  };

  const formatTime = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType?.toLowerCase()) {
      case 'game':
      case 'match':
        return '🏆';
      case 'practice':
      case 'training':
        return '⚽';
      case 'meeting':
        return '📋';
      default:
        return '📅';
    }
  };

  // Calculate total events in the next 15 days
  const totalEvents = upcomingDays.reduce((sum, day) => sum + day.eventCount, 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaCalendarWeek className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Next 15 Days</h3>
        </div>
        <span className={styles.eventCount}>
          {totalEvents} {totalEvents === 1 ? 'event' : 'events'}
        </span>
      </div>

      <div className={styles.content}>
        {/* Upcoming Days */}
        <div className={styles.weekDays}>
          {upcomingDays.map((day, idx) => (
            <button 
              key={idx}
              className={`
                ${styles.dayButton} 
                ${day.isToday ? styles.today : ''} 
                ${day.isSelected ? styles.selected : ''} 
                ${day.hasEvent ? styles.hasEvent : ''}
                ${day.isWeekend ? styles.weekend : ''}
              `}
              onClick={() => handleDayClick(day.date, day.hasEvent)}
              title={`${day.dayName} ${day.month} ${day.dayNum} - ${day.eventCount} event${day.eventCount !== 1 ? 's' : ''}`}
            >
              <span className={styles.dayName}>{day.dayName}</span>
              <span className={styles.dayNum}>{day.dayNum}</span>
              {day.hasEvent && (
                <div className={styles.eventDots}>
                  {Array.from({ length: Math.min(day.eventCount, 3) }).map((_, i) => (
                    <span key={i} className={styles.eventDot}></span>
                  ))}
                  {day.eventCount > 3 && <span className={styles.moreDots}>+</span>}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Selected Day Events */}
        {selectedDate && selectedDayEvents.length > 0 && (
          <div className={styles.dayEvents}>
            <div className={styles.dayEventsHeader}>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            <div className={styles.eventsList}>
              {selectedDayEvents.map(event => (
                <div 
                  key={event._id} 
                  className={styles.eventItem}
                  onClick={() => handleEventClick(event)}
                >
                  <span className={styles.eventIcon}>{getEventTypeIcon(event.type)}</span>
                  <div className={styles.eventInfo}>
                    <span className={styles.eventTitle}>{event.title}</span>
                    <div className={styles.eventMeta}>
                      <span className={styles.eventTime}>
                        <FaClock /> {formatTime(event.dateTime?.start)}
                      </span>
                      {event.location?.name && (
                        <span className={styles.eventLocation}>
                          <FaMapMarkerAlt /> {event.location.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedDate && selectedDayEvents.length === 0 && (
          <div className={styles.noEvents}>
            <span>No events on this day</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeekGlanceWidget;



